import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import {
  BookOpen,
  CalendarDays,
  CalendarPlus,
  ChevronRight,
  Clock,
  Heart,
  Home as HomeIcon,
  Megaphone,
  MapPin,
  Menu,
  Minus,
  List,
  Plus,
  Printer,
  Quote,
  Settings,
  Share2,
  Sparkles,
  Users,
} from "lucide-react";
import { RichDocument } from "@aledo-fca/editor";
import { api, ApiFailure, deviceToken, formatDate } from "./api";
import { reportClientEvent } from "./telemetry";

type Doc = { type: string; content?: any[] };
type Page<T> = { data: T[]; pagination: { total: number } };
type Study = {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  summary: string;
  publishedAt: string;
  documentJson?: Doc;
  seriesLabel?: string;
  coverImageUrl?: string;
  coverImageAlt?: string;
  featuredQuote?: { text: string; reference?: string | null } | null;
  usedAtEvents?: Array<{ slug: string; title: string; category: string; startAt: string; endAt?: string; location?: string; status: string }>;
};
type Announcement = {
  id: string;
  slug: string;
  title: string;
  summary: string;
  bodyJson: Doc;
  targetType?: "STUDY" | "EVENT" | "PRAYER" | null;
  targetSnapshotJson?: AnnouncementTarget | null;
  publishedAt: string;
};
type AnnouncementTarget = {
  id: string;
  type: "STUDY" | "EVENT" | "PRAYER";
  title: string;
  excerpt: string;
  href: string;
  imageUrl?: string | null;
  imageAlt?: string | null;
  startAt?: string | null;
  location?: string | null;
};
type Prayer = {
  id: string;
  displayText: string;
  displayName?: string;
  anonymousDisplay: boolean;
  publishedAt: string;
  prayingCount: number;
};
type Occurrence = {
  id: string;
  slug: string;
  title: string;
  category: string;
  startAt: string;
  endAt?: string;
  location?: string;
  status: string;
  rescheduledFrom?: string;
  rsvpEnabled: boolean;
  rsvpClosesAt?: string;
  meetingTopic?: string;
  linkedStudyTitle?: string;
  linkedStudySlug?: string;
  linkedStudySummary?: string;
  linkedStudyCoverUrl?: string;
  linkedStudyCoverAlt?: string;
};
type Testimony = {
  id: string;
  title: string;
  publishedText: string;
  displayName?: string;
  anonymousDisplay: boolean;
  publishedAt: string;
};
function responsiveImage(url?: string) {
  if (!url?.startsWith("/api/v1/assets/") || !url.endsWith(".webp")) return {};
  return {
    srcSet: `${url}?w=480 480w, ${url}?w=960 960w, ${url}?w=1600 1600w`,
    sizes: "(max-width: 600px) 100vw, (max-width: 1000px) 70vw, 850px",
  };
}

function useLoad<T>(loader: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    loader()
      .then((v) => active && setData(v))
      .catch((e) => active && setError(e.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, deps);
  return { data, error, loading, setData };
}
function PageState({
  loading,
  error,
  children,
}: {
  loading: boolean;
  error: string;
  children: ReactNode;
}) {
  if (loading)
    return (
      <div className="skeleton-list" aria-label="Loading content" aria-busy="true">
        {[0, 1, 2].map((item) => <div className="skeleton-card" key={item}><span /><div><i /><i /><i /></div></div>)}
      </div>
    );
  if (error)
    return (
      <div className="notice error" role="alert">
        {error}
      </div>
    );
  return <>{children}</>;
}
const nav = [
  ["/", HomeIcon, "Home"],
  ["/studies", BookOpen, "Studies"],
  ["/prayer", Heart, "Prayer"],
  ["/events", CalendarDays, "Events"],
  ["/more", Menu, "More"],
] as const;
function Header() {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const update = () => setCompact(scrollY > 28);
    addEventListener("scroll", update, { passive: true });
    update();
    return () => removeEventListener("scroll", update);
  }, []);
  return (
    <header className={`site-header${compact ? " compact" : ""}`}>
      <Link className="brand" to="/" aria-label="Aledo FCA home">
        <img className="brand-mark" src="/fca-logo.png" alt="" />
        <span>
          Aledo <b>FCA</b>
        </span>
      </Link>
      <nav className="site-nav" aria-label="Primary">
        {nav.map(([to, _Icon, label]) => (
          <NavLink key={to} to={to} end={to === "/"}>
            {label}
          </NavLink>
        ))}
      </nav>
      <Link className="header-action" to="/settings/notifications" aria-label="Notifications">
        <Megaphone size={18} /> Notifications
      </Link>
    </header>
  );
}
function BottomNav() {
  return (
    <nav className="bottom-nav" aria-label="Primary">
      {nav.map(([to, Icon, label]) => (
        <NavLink key={to} to={to} end={to === "/"} aria-label={label} title={label}>
          <Icon aria-hidden="true" />
        </NavLink>
      ))}
    </nav>
  );
}
function Layout({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    addEventListener("online", on);
    addEventListener("offline", off);
    return () => { removeEventListener("online", on); removeEventListener("offline", off); };
  }, []);
  return (
    <>
      <Header />
      {!online && <div className="public-offline" role="alert">You’re offline. Showing the latest saved public content on this device.</div>}
      <main id="main">{children}</main>
      <BottomNav />
      <footer>
        <div>
          <span className="footer-brand">Aledo FCA</span>
          <p>A welcoming place to grow in faith, together.</p>
        </div>
        <div>
          <Link to="/privacy">Privacy & safety</Link>
          <Link to="/settings/notifications">Install & notifications</Link>
        </div>
      </footer>
    </>
  );
}
function SectionTitle({
  eyebrow,
  title,
  copy,
}: {
  eyebrow?: string;
  title: string;
  copy?: string;
}) {
  return (
    <div className="section-heading">
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h1>{title}</h1>
      {copy && <p>{copy}</p>}
    </div>
  );
}
function Countdown({
  meeting,
  serverTime,
}: {
  meeting: any;
  serverTime: string;
}) {
  const offset = useMemo(
    () => new Date(serverTime).getTime() - Date.now(),
    [serverTime],
  );
  const [remaining, setRemaining] = useState(
    Math.max(0, new Date(meeting.startAt).getTime() - (Date.now() + offset)),
  );
  useEffect(() => {
    const tick = () =>
      setRemaining(
        Math.max(
          0,
          new Date(meeting.startAt).getTime() - (Date.now() + offset),
        ),
      );
    tick();
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const id = setInterval(tick, reduceMotion ? 60_000 : 1000);
    return () => clearInterval(id);
  }, [meeting.startAt, offset]);
  const total = Math.floor(remaining / 1000);
  const parts = [
    ["Days", Math.floor(total / 86400)],
    ["Hours", Math.floor((total % 86400) / 3600)],
    ["Minutes", Math.floor((total % 3600) / 60)],
    ["Seconds", total % 60],
  ];
  return (
    <div className="countdown" aria-label={`Countdown to ${meeting.title}`}>
      {parts.map(([label, value]) => (
        <div key={String(label)}>
          <strong>{String(value).padStart(2, "0")}</strong>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}
function calendarUrlFor(item: { startAt: string; endAt?: string | null; location?: string | null }, title: string, detailsUrl?: string) {
  const start = new Date(item.startAt);
  const end = item.endAt ? new Date(item.endAt) : new Date(start.getTime() + 60 * 60_000);
  const stamp = (date: Date) => date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${stamp(start)}/${stamp(end)}&location=${encodeURIComponent(item.location ?? "")}&details=${encodeURIComponent(detailsUrl ?? location.href)}`;
}
function Home() {
  const { data, error, loading } = useLoad(() => api<any>("/home"), []);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") location.reload();
    };
    document.addEventListener("visibilitychange", refresh);
    return () => document.removeEventListener("visibilitychange", refresh);
  }, []);
  return (
    <PageState loading={loading} error={error}>
      {data && (
        <div className="home-page">
          <section className="hero">
            <div className="hero-copy">
              <span className="eyebrow light">Faith • Community • Purpose</span>
              <h1>You belong here.</h1>
              <p>
                Explore this week's Bible study, share a prayer, and see what's
                happening with Aledo FCA.
              </p>
              {data.data.featuredStudy ? (
                <Link
                  className="button gold"
                  to={`/studies/${data.data.featuredStudy.slug}`}
                >
                  Read this week's study <ChevronRight />
                </Link>
              ) : (
                <Link className="button gold" to="/studies">
                  Explore Bible studies <ChevronRight />
                </Link>
              )}
            </div>
            <div className="hero-art" aria-hidden="true">
              <img src="/fca-logo.png" alt="" />
            </div>
          </section>
          <section className="meeting-panel">
            <div>
              <span className="eyebrow">Next FCA meeting</span>
              {data.data.nextMeeting ? (
                <>
                  <h2>{data.data.nextMeeting.title}</h2>
                  <p>
                    <Clock /> {formatDate(data.data.nextMeeting.startAt)}
                    {data.data.nextMeeting.location &&
                      ` · ${data.data.nextMeeting.location}`}
                  </p>
                </>
              ) : (
                <>
                  <h2>Next FCA meeting to be announced.</h2>
                  <p>Check back soon for the next gathering.</p>
                </>
              )}
            </div>
            {data.data.nextMeeting && (
              <div className="meeting-panel-actions"><Countdown meeting={data.data.nextMeeting} serverTime={data.serverTime} /><div className="meeting-link-row"><Link className="meeting-detail-link" to={`/events/${data.data.nextMeeting.slug}`}>Meeting details <ChevronRight /></Link><a className="meeting-detail-link" href={calendarUrlFor(data.data.nextMeeting, "Aledo FCA Meeting", `${location.origin}/events/${data.data.nextMeeting.slug}`)} target="_blank" rel="noreferrer"><CalendarPlus /> Add to calendar</a></div></div>
            )}
          </section>
          {data.data.featuredStudy && (
            <section
              className={`featured-grid ${
                !data.data.featuredStudy.featuredQuote &&
                !data.data.featuredStudy.coverImageUrl
                  ? "single"
                  : ""
              }`}
            >
              <div className="study-feature">
                <span className="eyebrow">This week's study</span>
                <h2>{data.data.featuredStudy.title}</h2>
                <p>{data.data.featuredStudy.summary}</p>
                <Link
                  className="text-link"
                  to={`/studies/${data.data.featuredStudy.slug}`}
                >
                  Open study <ChevronRight />
                </Link>
              </div>
              {data.data.featuredStudy.featuredQuote ? (
                <blockquote className="featured-quote">
                  <span>{data.data.featuredStudy.featuredQuote.text}</span>
                  {data.data.featuredStudy.featuredQuote.reference && (
                    <cite>
                      {data.data.featuredStudy.featuredQuote.reference}
                    </cite>
                  )}
                </blockquote>
              ) : (
                data.data.featuredStudy.coverImageUrl && (
                  <div className="featured-study-image">
                    <img
                      src={data.data.featuredStudy.coverImageUrl}
                      {...responsiveImage(data.data.featuredStudy.coverImageUrl)}
                      alt={data.data.featuredStudy.coverImageAlt ?? ""}
                    />
                  </div>
                )
              )}
            </section>
          )}
          <section className="content-section">
            <div className="heading-row">
              <h2>What’s happening</h2>
              <Link to="/events">All events</Link>
            </div>
            <div className="card-grid">
              {data.data.events.length ? (
                data.data.events
                  .slice(0, 3)
                  .map((event: Occurrence) => (
                    <EventCard event={event} key={event.id} />
                  ))
              ) : (
                <Empty
                  icon={<CalendarDays />}
                  title="No upcoming events yet"
                  copy="New gatherings will appear here as soon as leaders publish them."
                />
              )}
            </div>
          </section>
          <section className="content-section tint">
            <div className="heading-row">
              <h2>Latest announcements</h2>
              <Link to="/announcements">View all</Link>
            </div>
            {data.data.announcements.length ? (
              <div className="announcement-list">
                {data.data.announcements.map((a: Announcement) => (
                  <article key={a.id}>
                    <div className="announcement-note">
                      <Megaphone />
                      <p>{a.summary}</p>
                    </div>
                    {a.targetSnapshotJson ? (
                      <AnnouncementQuote target={a.targetSnapshotJson} compact />
                    ) : (
                      <Link className="legacy-announcement" to="/announcements">
                        <b>{a.title}</b>
                        <ChevronRight />
                      </Link>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <Empty
                icon={<Megaphone />}
                title="No announcements yet"
                copy="Updates from Aledo FCA will show up here."
              />
            )}
          </section>
          <section className="prayer-invite">
            <div>
              <span className="eyebrow light">You’re not alone</span>
              <h2>How can we pray for you?</h2>
              <p>
                Share publicly after leader review, or send a private request
                seen only by authorized FCA leaders.
              </p>
            </div>
            <Link className="button light" to="/prayer/submit">
              Share a prayer request
            </Link>
          </section>
        </div>
      )}
    </PageState>
  );
}
function EventCard({ event }: { event: Occurrence }) {
  return (
    <Link
      className={`event-card ${event.status === "CANCELED" ? "canceled" : ""}`}
      to={`/events/${event.slug}`}
    >
      <div className="date-badge">
        <span>
          {new Intl.DateTimeFormat("en-US", {
            month: "short",
            timeZone: "America/Chicago",
          }).format(new Date(event.startAt))}
        </span>
        <b>
          {new Intl.DateTimeFormat("en-US", {
            day: "2-digit",
            timeZone: "America/Chicago",
          }).format(new Date(event.startAt))}
        </b>
      </div>
      <div>
        <div className="meta">
          {event.category === "FCA_MEETING" ? "FCA Meeting" : "Event"}{" "}
          {event.status === "CANCELED" && <em>Canceled</em>}
          {event.rescheduledFrom && <em>Rescheduled</em>}
        </div>
        <h3>{event.title}</h3>
        {event.meetingTopic && <b className="event-card-topic">{event.meetingTopic}</b>}
        <p>
          {formatDate(event.startAt, {
            weekday: "short",
            hour: "numeric",
            minute: "2-digit",
          })}
          {event.location && ` · ${event.location}`}
        </p>
      </div>
      <ChevronRight />
    </Link>
  );
}
function AnnouncementQuote({
  target,
  compact = false,
}: {
  target: AnnouncementTarget;
  compact?: boolean;
}) {
  const label =
    target.type === "STUDY"
      ? "Bible study"
      : target.type === "EVENT"
        ? "Event"
        : "Prayer";
  return (
    <Link
      className={`announcement-quote${compact ? " compact" : ""}`}
      to={target.href}
    >
      {target.imageUrl && (
        <img src={target.imageUrl} {...responsiveImage(target.imageUrl)} alt={target.imageAlt ?? ""} />
      )}
      <div>
        <span>{label}</span>
        <h3>{target.title}</h3>
        {target.startAt && (
          <small>
            {formatDate(target.startAt, {
              weekday: "short",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
            {target.location && ` · ${target.location}`}
          </small>
        )}
        {target.excerpt && <p>{target.excerpt}</p>}
      </div>
      <ChevronRight />
    </Link>
  );
}
function Empty({
  icon,
  title,
  copy,
  action,
}: {
  icon: ReactNode;
  title: string;
  copy: string;
  action?: { to: string; label: string };
}) {
  return (
    <div className="empty">
      {icon}
      <h3>{title}</h3>
      <p>{copy}</p>
      {action && <Link className="button secondary" to={action.to}>{action.label}<ChevronRight /></Link>}
    </div>
  );
}

function Studies() {
  const { data, error, loading } = useLoad(
    () => api<Page<Study>>("/studies?limit=50"),
    [],
  );
  return (
    <section className="page">
      <SectionTitle
        eyebrow="Grow deeper"
        title="Bible studies"
        copy="Read this week's study or explore the archive at your own pace."
      />
      <PageState loading={loading} error={error}>
        <div className="story-grid">
          {data?.data.map((s, i) => (
            <Link
              className={`story-card ${i === 0 ? "featured" : ""}`}
              to={`/studies/${s.slug}`}
              key={s.id}
            >
              <div className="story-image">
                {s.coverImageUrl ? (
                  <img src={s.coverImageUrl} {...responsiveImage(s.coverImageUrl)} alt={s.coverImageAlt ?? ""} />
                ) : (
                  <BookOpen />
                )}
                <span>{s.seriesLabel ?? "Weekly Study"}</span>
              </div>
              <div>
                <span className="meta">
                  {formatDate(s.publishedAt, { dateStyle: "medium" })}
                </span>
                <h2>{s.title}</h2>
                <p>{s.summary}</p>
                <span className="text-link">
                  Read study <ChevronRight />
                </span>
              </div>
            </Link>
          ))}
        </div>
        {!data?.data.length && (
          <Empty
            icon={<BookOpen />}
            title="Studies are coming soon"
            copy="Published studies will be collected here."
            action={{ to: "/events", label: "See upcoming FCA events" }}
          />
        )}
      </PageState>
    </section>
  );
}
function StudyDetail() {
  const { slug } = useParams();
  const articleRef = useRef<HTMLElement>(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [resumeProgress] = useState(() => Number(localStorage.getItem(`aledo-study-progress:${slug}`) ?? 0));
  const [textScale, setTextScale] = useState(() => Number(localStorage.getItem("aledo-study-text-scale") ?? 1));
  const [outline, setOutline] = useState<Array<{ id: string; label: string }>>([]);
  const [showOutline, setShowOutline] = useState(false);
  const { data, error, loading } = useLoad(
    () => api<{ data: Study }>(`/studies/${slug}`),
    [slug],
  );
  useEffect(() => {
    const updateProgress = () => {
      const article = articleRef.current;
      if (!article) return;
      const start = article.offsetTop;
      const distance = Math.max(1, article.offsetHeight - window.innerHeight);
      const progress = Math.max(0, Math.min(1, (window.scrollY - start) / distance));
      setReadingProgress(progress);
      localStorage.setItem(`aledo-study-progress:${slug}`, String(progress));
    };
    addEventListener("scroll", updateProgress, { passive: true });
    updateProgress();
    return () => removeEventListener("scroll", updateProgress);
  }, [slug, data]);
  useEffect(() => {
    const article = articleRef.current;
    if (!article || !data) return;
    const headings = [...article.querySelectorAll<HTMLElement>(".rich-document h2, .rich-document h3")];
    const used = new Set<string>();
    setOutline(headings.map((heading, index) => {
      const base = (heading.textContent ?? `section-${index + 1}`).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || `section-${index + 1}`;
      let id = base;
      let suffix = 2;
      while (used.has(id)) id = `${base}-${suffix++}`;
      used.add(id);
      heading.id = id;
      return { id, label: heading.textContent?.trim() || `Section ${index + 1}` };
    }));
  }, [data]);
  useEffect(() => {
    localStorage.setItem("aledo-study-text-scale", String(textScale));
  }, [textScale]);
  function resumeReading() {
    const article = articleRef.current;
    if (!article) return;
    const distance = Math.max(1, article.offsetHeight - window.innerHeight);
    window.scrollTo({ top: article.offsetTop + distance * resumeProgress, behavior: "smooth" });
  }
  async function shareStudy() {
    if (navigator.share) await navigator.share({ title: data?.data.title ?? "Aledo FCA Bible study", url: location.href }).catch(() => undefined);
    else await navigator.clipboard.writeText(location.href);
  }
  return (
    <section className="reading-page">
      <div className="reading-progress" aria-label={`${Math.round(readingProgress * 100)}% read`}><span style={{ width: `${readingProgress * 100}%` }} /></div>
      <PageState loading={loading} error={error}>
        {data && (
          <article ref={articleRef} style={{ "--study-text-scale": textScale } as React.CSSProperties}>
            <Link className="back-link" to="/studies">
              ← All studies
            </Link>
            <header>
              <span className="eyebrow">
                {data.data.seriesLabel ?? "Bible study"}
              </span>
              <h1>{data.data.title}</h1>
              {data.data.subtitle && (
                <p className="subtitle">{data.data.subtitle}</p>
              )}
              <p className="meta">
                Published{" "}
                {formatDate(data.data.publishedAt, { dateStyle: "long" })}
              </p>
              {resumeProgress > .05 && resumeProgress < .95 && <button type="button" className="continue-reading" onClick={resumeReading}>Continue where you left off · {Math.round(resumeProgress * 100)}%</button>}
              <div className="study-reader-tools" aria-label="Reading tools">
                {outline.length > 0 && <button type="button" aria-expanded={showOutline} onClick={() => setShowOutline((open) => !open)}><List /> Contents</button>}
                <div role="group" aria-label="Text size"><button type="button" aria-label="Make text smaller" disabled={textScale <= .9} onClick={() => setTextScale((size) => Math.max(.9, size - .1))}><Minus /></button><span>Text size</span><button type="button" aria-label="Make text larger" disabled={textScale >= 1.3} onClick={() => setTextScale((size) => Math.min(1.3, size + .1))}><Plus /></button></div>
                <button type="button" onClick={() => void shareStudy()}><Share2 /> Share</button>
                <button type="button" onClick={() => print()}><Printer /> Print</button>
              </div>
              {showOutline && <nav className="study-outline" aria-label="Study contents">{outline.map((item) => <a href={`#${item.id}`} key={item.id} onClick={() => setShowOutline(false)}>{item.label}</a>)}</nav>}
              {data.data.coverImageUrl && (
                <img
                  className="study-cover"
                  src={data.data.coverImageUrl}
                  {...responsiveImage(data.data.coverImageUrl)}
                  alt={data.data.coverImageAlt ?? ""}
                />
              )}
            </header>
            <RichDocument document={data.data.documentJson!} />
            {data.data.usedAtEvents?.length ? <section className="study-meeting-history"><span className="eyebrow">FCA connection</span><h2>{data.data.usedAtEvents.some((event) => new Date(event.startAt) > new Date()) ? "Coming up at FCA" : "Used at FCA"}</h2><div>{data.data.usedAtEvents.map((event) => <Link to={`/events/${event.slug}`} key={`${event.slug}-${event.startAt}`}><CalendarDays /><span><b>{event.category === "FCA_MEETING" ? "FCA Meeting" : event.title}</b><small>{formatDate(event.startAt, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}{event.location ? ` · ${event.location}` : ""}</small></span><ChevronRight /></Link>)}</div></section> : null}
            <div className="article-end">
              <Sparkles />
              <h2>Keep the conversation going</h2>
              <p>Share what stood out with a trusted friend or FCA leader.</p>
            </div>
          </article>
        )}
      </PageState>
    </section>
  );
}

function Prayer() {
  const { data, error, loading, setData } = useLoad(
    () => api<Page<Prayer>>("/prayers?limit=50"),
    [],
  );
  async function pray(item: Prayer) {
    try {
      const result = await api<{ data: { prayingCount: number } }>(
        `/prayers/${item.id}/im-praying`,
        {
          method: "POST",
          body: JSON.stringify({ token: deviceToken("aledo-prayer-token") }),
        },
      );
      setData((current) =>
        current
          ? {
              ...current,
              data: current.data.map((p) =>
                p.id === item.id
                  ? { ...p, prayingCount: result.data.prayingCount }
                  : p,
              ),
            }
          : current,
      );
    } catch {
      return;
    }
  }
  return (
    <section className="page">
      <div className="heading-split">
        <SectionTitle
          eyebrow="Prayer wall"
          title="Pray with us"
          copy="These requests were shared for our community to lift up together."
        />
        <Link className="button primary" to="/prayer/submit">
          Share a request
        </Link>
      </div>
      <div className="safety-note">
        <Heart />
        <p>
          Please avoid names or private details about others. This wall is not
          an emergency service—reach out to a trusted adult or local emergency
          services for urgent help.
        </p>
      </div>
      <PageState loading={loading} error={error}>
        <div className="prayer-grid">
          {data?.data.map((p) => (
            <article className="prayer-card" key={p.id}>
              <Quote />
              <p>{p.displayText}</p>
              <div>
                <span>
                  {p.anonymousDisplay
                    ? "Anonymous"
                    : p.displayName || "Anonymous"}{" "}
                  · {formatDate(p.publishedAt, { dateStyle: "medium" })}
                </span>
                <button className="pray-button" onClick={() => void pray(p)}>
                  <Heart /> I’m praying <b>{p.prayingCount}</b>
                </button>
              </div>
            </article>
          ))}
        </div>
        {!data?.data.length && (
          <Empty
            icon={<Heart />}
            title="The prayer wall is quiet"
            copy="You can be the first to share a request for our community to pray over."
            action={{ to: "/prayer/submit", label: "Share a prayer request" }}
          />
        )}
      </PageState>
    </section>
  );
}
function PrayerForm() {
  const [state, setState] = useState({
    text: "",
    displayName: "",
    anonymousDisplay: true,
    visibility: "PUBLIC_WALL",
  });
  const [status, setStatus] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const prayerDraftRef = useRef("");
  const prayerSubmittedRef = useRef(false);
  prayerDraftRef.current = state.text;
  prayerSubmittedRef.current = submitted;
  useEffect(() => () => {
    if (prayerDraftRef.current.trim() && !prayerSubmittedRef.current) reportClientEvent("form_abandoned");
  }, []);
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("aledo-prayer-draft") ?? "null");
      if (saved) setState(saved);
    } catch { /* Ignore a corrupt local draft. */ }
  }, []);
  useEffect(() => {
    if (!submitted) localStorage.setItem("aledo-prayer-draft", JSON.stringify(state));
  }, [state, submitted]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!reviewing) {
      setReviewing(true);
      scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setStatus("Sending…");
    try {
      await api("/prayers", {
        method: "POST",
        body: JSON.stringify({ ...state, website: "" }),
      });
      localStorage.removeItem("aledo-prayer-draft");
      setSubmitted(true);
      setStatus("Your prayer request was received. A leader will review it with care.");
    } catch (e) {
      setStatus(e instanceof ApiFailure ? e.message : "Please try again.");
    }
  }
  if (submitted) return <section className="form-page"><div className="submission-success" role="status"><Heart /><span className="eyebrow">Received with care</span><h1>Thank you for sharing.</h1><p>{status}</p><div className="button-row"><Link className="button primary" to="/prayer">Return to prayer wall</Link><Link className="button secondary" to="/">Go home</Link></div></div></section>;
  return (
    <section className="form-page">
      <Link className="back-link" to="/prayer">
        ← Prayer wall
      </Link>
      <SectionTitle
        eyebrow="Share a request"
        title="How can we pray?"
        copy="You choose whether this goes to the public wall after review or stays private with authorized leaders."
      />
      <form onSubmit={submit}>
        {reviewing ? <div className="submission-review"><span className="eyebrow">Review before sending</span><h2>Your prayer request</h2><p>{state.text}</p><dl><dt>Visibility</dt><dd>{state.visibility === "PUBLIC_WALL" ? "Public after leader review" : "Authorized FCA leaders only"}</dd><dt>Name</dt><dd>{state.anonymousDisplay ? "Anonymous if published" : state.displayName || "Anonymous"}</dd></dl><button type="button" className="button secondary" onClick={() => setReviewing(false)}>Go back and edit</button></div> : <>
        <label>
          Prayer request
          <textarea
            required
            minLength={10}
            maxLength={3000}
            rows={6}
            value={state.text}
            onChange={(e) => setState({ ...state, text: e.target.value })}
            placeholder="Share only what you are comfortable sharing…"
          />
          <small className="character-count">{state.text.length > 2500 ? `${3000 - state.text.length} characters remaining` : "Your draft is saved on this device."}</small>
        </label>
        <fieldset>
          <legend>Who can see this?</legend>
          <label className="choice">
            <input
              type="radio"
              name="visibility"
              checked={state.visibility === "PUBLIC_WALL"}
              onChange={() => setState({ ...state, visibility: "PUBLIC_WALL" })}
            />
            <span>
              <b>Public prayer wall</b>
              <small>A leader will review it before it appears publicly.</small>
            </span>
          </label>
          <label className="choice">
            <input
              type="radio"
              name="visibility"
              checked={state.visibility === "LEADERS_ONLY"}
              onChange={() =>
                setState({ ...state, visibility: "LEADERS_ONLY" })
              }
            />
            <span>
              <b>FCA leaders only</b>
              <small>
                Kept private and visible only to authorized leaders.
              </small>
            </span>
          </label>
        </fieldset>
        <label>
          Your name <span className="optional">optional</span>
          <input
            maxLength={80}
            value={state.displayName}
            onChange={(e) =>
              setState({ ...state, displayName: e.target.value })
            }
          />
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={state.anonymousDisplay}
            onChange={(e) =>
              setState({ ...state, anonymousDisplay: e.target.checked })
            }
          />
          Show me as “Anonymous” if published
        </label>
        <input
          className="honeypot"
          aria-hidden="true"
          tabIndex={-1}
          autoComplete="off"
          name="website"
        />
        <div className="notice">
          <b>A quick safety note</b>
          <p>
            Avoid private details about other people. For immediate danger,
            contact emergency services or a trusted adult—this form is not
            monitored as an emergency line.
          </p>
        </div>
        </>}
        <button className="button primary" disabled={status === "Sending…"}>
          {reviewing ? "Confirm and send" : "Review request"}
        </button>
        <p className="form-status" role="status">
          {status}
        </p>
      </form>
    </section>
  );
}

function Events() {
  const { data, error, loading } = useLoad(
    () => api<{ data: Occurrence[] }>("/events"),
    [],
  );
  const upcoming =
    data?.data.filter((e) => new Date(e.startAt) > new Date()) ?? [];
  const past =
    data?.data.filter((e) => new Date(e.startAt) <= new Date()).reverse() ?? [];
  const [showPast, setShowPast] = useState(false);
  return (
    <section className="page">
      <SectionTitle
        eyebrow="Gather together"
        title="Events"
        copy="FCA meetings and other upcoming gatherings, all in one place."
      />
      <PageState loading={loading} error={error}>
        <div className="event-list">
          {upcoming.map((e) => (
            <EventCard event={e} key={e.id} />
          ))}
        </div>
        {!upcoming.length && (
          <Empty
            icon={<CalendarDays />}
            title="No upcoming events"
            copy="New events will appear here after leaders publish them."
            action={{ to: "/studies", label: "Read Bible studies" }}
          />
        )}
        {past.length > 0 && (
          <>
            <button className="past-events-toggle" type="button" aria-expanded={showPast} onClick={() => setShowPast((current) => !current)}><span><b>Past events</b><small>{past.length} previous {past.length === 1 ? "event" : "events"}</small></span><ChevronRight className={showPast ? "open" : ""} /></button>
            {showPast && <div className="event-list muted">{past.map((e) => <EventCard event={e} key={e.id} />)}</div>}
          </>
        )}
      </PageState>
    </section>
  );
}
function EventDetail() {
  const { slug } = useParams();
  const { data, error, loading } = useLoad(
    () => api<any>(`/events/${slug}`),
    [slug],
  );
  return (
    <section className="page">
      <PageState loading={loading} error={error}>
        {data && (
          <>
            <Link className="back-link" to="/events">
              ← All events
            </Link>
            <SectionTitle
              eyebrow={
                data.data.category === "FCA_MEETING" ? "FCA meeting" : "Event"
              }
              title={data.data.title}
            />
            <RichDocument document={data.data.descriptionJson} />
            <div className="event-list detail-list">
              {data.data.occurrences.map((o: any) => (
                <article className="occurrence" key={o.id}>
                  <div>
                    {o.status === "CANCELED" && <div className="event-state-banner canceled">This occurrence was canceled and will not meet at the time shown below.</div>}
                    {o.rescheduledFrom && o.status !== "CANCELED" && <div className="event-state-banner rescheduled">This meeting was rescheduled. The date and time below are the current schedule.</div>}
                    <span className={`pill ${o.status.toLowerCase()}`}>
                      {o.status === "CANCELED"
                        ? "Canceled"
                        : o.rescheduledFrom
                          ? "Rescheduled"
                          : "Scheduled"}
                    </span>
                    <h2>
                      {formatDate(o.startAt, {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </h2>
                    {o.location && <p>{o.location}</p>}
                    <EventActions occurrence={o} event={data.data} />
                    {o.meetingTopic && <p className="occurrence-topic"><b>This week:</b> {o.meetingTopic}</p>}
                    {o.linkedStudySlug && <Link className="public-linked-study" to={`/studies/${o.linkedStudySlug}`}>{o.linkedStudyCoverUrl && <img src={o.linkedStudyCoverUrl} {...responsiveImage(o.linkedStudyCoverUrl)} alt={o.linkedStudyCoverAlt ?? ""} />}<span><small>Bible study</small><b>{o.linkedStudyTitle}</b>{o.linkedStudySummary && <p>{o.linkedStudySummary}</p>}<em>Read the study <ChevronRight /></em></span></Link>}
                  </div>
                  {o.status === "SCHEDULED" && o.rsvpEnabled && (
                    <Rsvp occurrence={o} event={data.data} />
                  )}
                </article>
              ))}
            </div>
          </>
        )}
      </PageState>
    </section>
  );
}
function EventActions({ occurrence, event }: { occurrence: any; event: any }) {
  const title = event.category === "FCA_MEETING" ? "Aledo FCA Meeting" : event.title;
  const calendarUrl = calendarUrlFor(occurrence, title);
  async function share() {
    const payload = { title, text: `${title} — ${formatDate(occurrence.startAt)}`, url: location.href };
    if (navigator.share) await navigator.share(payload).catch(() => undefined);
    else await navigator.clipboard.writeText(location.href);
  }
  return <div className="public-event-actions"><a className="button secondary" href={calendarUrl} target="_blank" rel="noreferrer"><CalendarPlus /> Add to calendar</a>{occurrence.mapUrl && <a className="button secondary" href={occurrence.mapUrl} target="_blank" rel="noreferrer"><MapPin /> Open map</a>}<button className="button secondary" type="button" onClick={() => void share()}><Share2 /> Share</button></div>;
}
function Rsvp({ occurrence, event }: { occurrence: any; event: any }) {
  const [state, setState] = useState({ name: "", contact: "", quantity: 1 });
  const [status, setStatus] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    setStatus("Sending…");
    try {
      await api(`/event-occurrences/${occurrence.id}/rsvps`, {
        method: "POST",
        body: JSON.stringify({
          ...state,
          submissionKey: deviceToken(`aledo-rsvp-${occurrence.id}`),
          website: "",
        }),
      });
      setStatus("You’re on the list! Your RSVP has been received.");
    } catch (e) {
      setStatus(e instanceof ApiFailure ? e.message : "Please try again.");
    }
  }
  return (
    <form className="rsvp" onSubmit={submit}>
      <h3>RSVP</h3>
      <p>No account needed.</p>
      <label>
        Name
        <input
          required
          minLength={2}
          value={state.name}
          onChange={(e) => setState({ ...state, name: e.target.value })}
        />
      </label>
      {event.contactEnabled && (
        <label>
          Contact information <span className="optional">optional</span>
          <input
            value={state.contact}
            onChange={(e) => setState({ ...state, contact: e.target.value })}
          />
        </label>
      )}
      {event.partySizeEnabled && (
        <label>
          Party size
          <input
            type="number"
            min={1}
            max={10}
            value={state.quantity}
            onChange={(e) =>
              setState({ ...state, quantity: Number(e.target.value) })
            }
          />
        </label>
      )}
      <button className="button primary">Send RSVP</button>
      <p role="status">{status}</p>
    </form>
  );
}

function More() {
  return (
    <section className="page">
      <SectionTitle
        eyebrow="Explore"
        title="More from Aledo FCA"
        copy="Stories, updates, and settings for your experience."
      />
      <h2 className="more-group-title">Stories</h2><div className="more-grid">
        <Link to="/testimonies">
          <Quote />
          <div>
            <h2>Testimonies</h2>
            <p>Stories of faith and encouragement from students.</p>
          </div>
          <ChevronRight />
        </Link>
      </div>
      <h2 className="more-group-title">Updates</h2><div className="more-grid">
        <Link to="/announcements">
          <Megaphone />
          <div>
            <h2>Announcements</h2>
            <p>Catch up on recent FCA news and updates.</p>
          </div>
          <ChevronRight />
        </Link>
      </div>
      <h2 className="more-group-title">App settings</h2><div className="more-grid">
        <Link to="/settings/notifications">
          <Settings />
          <div>
            <h2>Install & notifications</h2>
            <p>Add Aledo FCA to your home screen and choose notifications.</p>
          </div>
          <ChevronRight />
        </Link>
        <Link to="/privacy">
          <Users />
          <div>
            <h2>Privacy & safety</h2>
            <p>How submissions, personal details, and safety are handled.</p>
          </div>
          <ChevronRight />
        </Link>
      </div>
      <Link className="button primary" to="/testimonies/submit">
        Share your testimony
      </Link>
    </section>
  );
}
function Testimonies() {
  const { data, error, loading } = useLoad(
    () => api<Page<Testimony>>("/testimonies?limit=50"),
    [],
  );
  return (
    <section className="page">
      <div className="heading-split">
        <SectionTitle
          eyebrow="Stories of faith"
          title="Testimonies"
          copy="Real stories shared to encourage our community."
        />
        <Link className="button primary" to="/testimonies/submit">
          Share your story
        </Link>
      </div>
      <PageState loading={loading} error={error}>
        <div className="testimony-list">
          {data?.data.map((t) => (
            <article key={t.id}>
              <Quote />
              <h2>{t.title}</h2>
              <p>{t.publishedText}</p>
              <span>
                —{" "}
                {t.anonymousDisplay
                  ? "Anonymous"
                  : t.displayName || "Anonymous"}{" "}
                · {formatDate(t.publishedAt, { dateStyle: "medium" })}
              </span>
            </article>
          ))}
        </div>
        {!data?.data.length && (
          <Empty
            icon={<Quote />}
            title="No published testimonies yet"
            copy="Approved stories will appear here."
            action={{ to: "/testimonies/submit", label: "Share your testimony" }}
          />
        )}
      </PageState>
    </section>
  );
}
function TestimonyForm() {
  const [state, setState] = useState({
    title: "",
    text: "",
    displayName: "",
    anonymousDisplay: true,
  });
  const [status, setStatus] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const testimonyDraftRef = useRef("");
  const testimonySubmittedRef = useRef(false);
  testimonyDraftRef.current = state.text;
  testimonySubmittedRef.current = submitted;
  useEffect(() => () => {
    if (testimonyDraftRef.current.trim() && !testimonySubmittedRef.current) reportClientEvent("form_abandoned");
  }, []);
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("aledo-testimony-draft") ?? "null");
      if (saved) setState(saved);
    } catch { /* Ignore a corrupt local draft. */ }
  }, []);
  useEffect(() => {
    if (!submitted) localStorage.setItem("aledo-testimony-draft", JSON.stringify(state));
  }, [state, submitted]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!reviewing) {
      setReviewing(true);
      scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setStatus("Sending…");
    try {
      await api("/testimonies", {
        method: "POST",
        body: JSON.stringify({ ...state, website: "" }),
      });
      localStorage.removeItem("aledo-testimony-draft");
      setSubmitted(true);
      setStatus("Thank you. Your testimony was sent for leader review.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Please try again.");
    }
  }
  if (submitted) return <section className="form-page"><div className="submission-success" role="status"><Sparkles /><span className="eyebrow">Story received</span><h1>Thank you for sharing.</h1><p>{status}</p><div className="button-row"><Link className="button primary" to="/testimonies">View testimonies</Link><Link className="button secondary" to="/">Go home</Link></div></div></section>;
  return (
    <section className="form-page">
      <Link className="back-link" to="/testimonies">
        ← Testimonies
      </Link>
      <SectionTitle
        eyebrow="Your story matters"
        title="Share your testimony"
        copy="Every submission is reviewed by a leader before it can be published."
      />
      <form onSubmit={submit}>
        {reviewing ? <div className="submission-review"><span className="eyebrow">Review before sending</span><h2>{state.title}</h2><p>{state.text}</p><dl><dt>Published name</dt><dd>{state.anonymousDisplay ? "Anonymous" : state.displayName || "Anonymous"}</dd><dt>Review</dt><dd>A leader must approve this before it appears publicly.</dd></dl><button type="button" className="button secondary" onClick={() => setReviewing(false)}>Go back and edit</button></div> : <>
        <label>
          Title
          <input
            required
            minLength={3}
            maxLength={160}
            value={state.title}
            onChange={(e) => setState({ ...state, title: e.target.value })}
          />
        </label>
        <label>
          Your testimony
          <textarea
            required
            minLength={20}
            maxLength={10000}
            rows={7}
            value={state.text}
            onChange={(e) => setState({ ...state, text: e.target.value })}
          />
          <small className="character-count">{state.text.length > 9000 ? `${10000 - state.text.length} characters remaining` : "Your draft is saved on this device."}</small>
        </label>
        <label>
          Your name <span className="optional">optional</span>
          <input
            maxLength={80}
            value={state.displayName}
            onChange={(e) =>
              setState({ ...state, displayName: e.target.value })
            }
          />
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={state.anonymousDisplay}
            onChange={(e) =>
              setState({ ...state, anonymousDisplay: e.target.checked })
            }
          />
          Publish as “Anonymous” if approved
        </label>
        <div className="notice">
          <p>
            If approved, your story will be publicly visible and may appear in
            search engines. Please leave out sensitive details and information
            about other minors.
          </p>
        </div>
        </>}
        <button className="button primary" disabled={status === "Sending…"}>{reviewing ? "Confirm and submit" : "Review testimony"}</button>
        <p role="status">{status}</p>
      </form>
    </section>
  );
}
function Announcements() {
  const { data, error, loading } = useLoad(
    () => api<Page<Announcement>>("/announcements?limit=50"),
    [],
  );
  return (
    <section className="page">
      <SectionTitle
        eyebrow="Stay in the loop"
        title="Announcements"
        copy="The latest news and updates from Aledo FCA."
      />
      <PageState loading={loading} error={error}>
        <div className="announcement-page">
          {data?.data.map((a) => (
            <article key={a.id}>
              <span className="meta">
                {formatDate(a.publishedAt, { dateStyle: "long" })}
              </span>
              {a.targetSnapshotJson ? (
                <>
                  <p className="announcement-message">{a.summary}</p>
                  <AnnouncementQuote target={a.targetSnapshotJson} />
                </>
              ) : (
                <>
                  <h2>{a.title}</h2>
                  <p className="lead">{a.summary}</p>
                  <RichDocument document={a.bodyJson} />
                </>
              )}
            </article>
          ))}
        </div>
        {!data?.data.length && (
          <Empty
            icon={<Megaphone />}
            title="No announcements yet"
            copy="Updates from leaders will appear here."
            action={{ to: "/events", label: "See upcoming events" }}
          />
        )}
      </PageState>
    </section>
  );
}
function Notifications() {
  const [status, setStatus] = useState("Checking support…");
  const [config, setConfig] = useState<any>();
  useEffect(() => {
    void api<any>("/config").then((c) => {
      setConfig(c.data);
      setStatus(
        !("serviceWorker" in navigator) || !("PushManager" in window)
          ? "Push notifications are not supported in this browser."
          : Notification.permission === "granted"
            ? "Notifications are enabled on this device."
            : "Notifications are currently off.",
      );
    });
  }, []);
  const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true;
  async function enable() {
    if (isiOS && !standalone) {
      setStatus(
        "On iPhone or iPad, first use Share → Add to Home Screen, then open Aledo FCA from the new icon.",
      );
      return;
    }
    if (!config?.vapidPublicKey) {
      setStatus(
        "Notifications are not configured yet. Please check back soon.",
      );
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus(
          "Permission was not granted. You can change this in browser or device settings.",
        );
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const key = Uint8Array.from(
        atob(config.vapidPublicKey.replace(/-/g, "+").replace(/_/g, "/")),
        (c) => c.charCodeAt(0),
      );
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key,
      });
      await api("/push-subscriptions", {
        method: "POST",
        body: JSON.stringify(sub),
      });
      setStatus("Notifications are enabled on this device.");
    } catch (e) {
      setStatus(
        e instanceof Error ? e.message : "Notifications could not be enabled.",
      );
    }
  }
  async function disable() {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api("/push-subscriptions", {
        method: "DELETE",
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });
      await sub.unsubscribe();
    }
    setStatus("Notifications are turned off for this app.");
  }
  return (
    <section className="form-page">
      <SectionTitle
        eyebrow="Stay connected"
        title="Install & notifications"
        copy="Aledo FCA works in your browser, and installation is always optional."
      />
      <div className="settings-card">
        <h2>Add to your home screen</h2>
        <p>
          <b>iPhone/iPad:</b> open the Share menu in Safari, choose “Add to Home
          Screen,” then open the app from that icon.
        </p>
        <p>
          <b>Android/Desktop:</b> use your browser’s Install option in the
          address bar or menu.
        </p>
      </div>
      <div className="settings-card">
        <h2>Notifications</h2>
        <p>
          Notifications are only sent when a leader deliberately chooses to
          share a relevant new study or announcement. There are no automatic
          meeting reminders.
        </p>
        <div className="button-row">
          <button className="button primary" onClick={() => void enable()}>
            Enable notifications
          </button>
          <button className="button secondary" onClick={() => void disable()}>
            Unsubscribe
          </button>
        </div>
        <p className="form-status" role="status">
          {status}
        </p>
      </div>
    </section>
  );
}
function Privacy() {
  return (
    <section className="reading-page">
      <article>
        <header>
          <span className="eyebrow">Plain-language policy</span>
          <h1>Privacy & safety</h1>
          <p className="subtitle">
            Aledo FCA is designed to work without student accounts and to
            collect as little personal information as practical.
          </p>
        </header>
        <h2>Public submissions</h2>
        <p>
          Prayer requests submitted to the public wall and all testimonies are
          reviewed by an FCA leader before publication. If published, they may
          be visible to anyone and indexed by search engines. Choose “Anonymous”
          and avoid details about other people.
        </p>
        <h2>Private prayer requests</h2>
        <p>
          Requests marked “FCA leaders only” are restricted to authorized
          leaders. They are not shown on the public wall or included in public
          notifications.
        </p>
        <h2>Events and RSVP information</h2>
        <p>
          RSVP names and any event-specific contact details are visible only to
          authorized leaders. An RSVP is a form submission, not a verified
          identity or attendance record.
        </p>
        <h2>Notifications</h2>
        <p>
          A push subscription belongs to a browser or device, not a student
          account. It is used only to deliver notifications that the visitor
          chooses to receive.
        </p>
        <h2>Urgent concerns</h2>
        <p>
          This website is not monitored as an emergency or counseling service.
          For immediate danger, contact local emergency services and a trusted
          adult. For other urgent concerns, contact an FCA leader or trusted
          school staff member offline.
        </p>
      </article>
    </section>
  );
}
function NotFound() {
  return (
    <section className="page">
      <Empty
        icon={<Sparkles />}
        title="We couldn’t find that page"
        copy="The link may be old or the content may no longer be published."
      />
      <p className="center">
        <Link className="button primary" to="/">
          Go home
        </Link>
      </p>
    </section>
  );
}
export function App() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/studies" element={<Studies />} />
        <Route path="/studies/:slug" element={<StudyDetail />} />
        <Route path="/prayer" element={<Prayer />} />
        <Route path="/prayer/submit" element={<PrayerForm />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:slug" element={<EventDetail />} />
        <Route path="/more" element={<More />} />
        <Route path="/testimonies" element={<Testimonies />} />
        <Route path="/testimonies/submit" element={<TestimonyForm />} />
        <Route path="/announcements" element={<Announcements />} />
        <Route path="/settings/notifications" element={<Notifications />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}
